import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { uploadQueue } from "../lib/upload_queue"

// The longest edge of a photo as it leaves the browser. The server shrinks it
// further (to 1,600 px); this just keeps a 12 MP phone photo well under the 10 MB limit.
const MAX_EDGE = 2000
const JPEG_QUALITY = 0.85

// How long one upload request may take. A phone that loses its signal mid-upload
// leaves fetch waiting for minutes, and the queue sends one photo at a time, so a
// request that goes quiet would hold up every photo behind it. After this long it
// is aborted, which the queue treats as a network failure and retries.
const UPLOAD_TIMEOUT_MS = 60_000

// Photos of the workplace, per space. Every chosen file gets a tile that shows
// how its upload is going; the files go one by one through an uploadQueue, which
// takes care of retrying on a bad connection.
//
// The file inputs sit outside any <form> on purpose: photos are saved as they are
// uploaded, so choosing one is not an unsaved change (the unsaved-changes guard
// only watches forms).
//
// After the setup (reloadWhenDone) a saved photo only gets its link, its remove
// buttons and its place in the Foto's tab count once the server draws the page,
// so the page is drawn again when every upload is done.
export default class extends Controller {
  static targets = ["tile"]
  static values = { url: String, labels: Object, reloadWhenDone: Boolean }

  connect() {
    this.entries = new Map() // File => { space, tile, small, previewUrl }
    this.queue = uploadQueue({ upload: (file) => this.send(file), onChange: (item) => this.render(item) })
  }

  choose(event) {
    const input = event.currentTarget
    const grid = input.closest("[data-space-group]").querySelector("[data-photo-grid]")
    const files = [...input.files]
    input.value = "" // so choosing the same photo again still counts as a change

    files.forEach((file) => {
      const tile = this.tileTarget.content.firstElementChild.cloneNode(true)
      grid.append(tile)
      this.entries.set(file, { space: input.dataset.space, tile })
      tile.dataset.itemId = this.queue.add(file).id
    })
    this.reloadWhenSettled()
  }

  retry(event) {
    const tile = event.currentTarget.closest("[data-item-id]")
    this.queue.retry(Number(tile.dataset.itemId))
    this.reloadWhenSettled()
  }

  // Once the queue is idle and every photo on the page was saved, the page is
  // fetched again. Not while a photo failed or was refused: reloading would drop
  // its tile, which says why and (when it failed) can still try again.
  async reloadWhenSettled() {
    if (!this.reloadWhenDoneValue) return
    await this.queue.idle()

    const items = this.queue.items
    if (this.reloading || items.length === 0 || !items.every((item) => item.status === "done")) return
    this.reloading = true
    Turbo.visit(window.location.href, { action: "replace" })
  }

  // The uploader for the queue: shrink the photo (once), then post it.
  async send(file) {
    const entry = this.entries.get(file)
    entry.small ||= await shrink(file)
    this.preview(entry)

    const form = new FormData()
    form.append("photo[image]", entry.small, entry.small.name)
    form.append("photo[space]", entry.space)

    const response = await fetch(this.urlValue, {
      method: "POST", body: form, credentials: "same-origin", signal: AbortSignal.timeout(UPLOAD_TIMEOUT_MS),
      headers: { "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content, Accept: "application/json" }
    })
    const body = await response.json().catch(() => null)
    // A redirect means the session ended (or access was stopped): the answer is a page, not a photo.
    if (response.redirected) return { ok: false, status: 401, body: null }
    return { ok: response.ok, status: response.status, body }
  }

  render(item) {
    const entry = this.entries.get(item.file)
    if (!entry) return
    const { tile } = entry

    tile.dataset.status = item.status
    tile.querySelector("[data-tile-status]").textContent =
      item.status === "rejected" && item.body?.error ? item.body.error : this.labelsValue[item.status]
    tile.querySelector("[data-tile-retry]").hidden = item.status !== "failed"

    if (item.status === "done" && item.body?.id) {
      tile.dataset.photoId = item.body.id
      this.showThumbnail(entry, item.body.thumb_url)
    }
  }

  // Shows the photo as it is about to be sent, so the tile has a picture at once.
  preview(entry) {
    if (!entry.previewUrl) entry.previewUrl = URL.createObjectURL(entry.small)
    this.setImage(entry.tile, entry.previewUrl)
  }

  // Once saved, the small server-made thumbnail replaces the preview, which
  // frees the (much bigger) one. If it can't be loaded the preview stays.
  showThumbnail(entry, url) {
    if (!url) return
    const probe = new Image()
    probe.onload = () => {
      this.setImage(entry.tile, url)
      if (entry.previewUrl) URL.revokeObjectURL(entry.previewUrl)
      entry.previewUrl = null
    }
    probe.src = url
  }

  setImage(tile, url) {
    const image = tile.querySelector("[data-tile-image]")
    image.onload = () => { image.hidden = false }
    image.src = url
  }
}

// The photo as a JPEG of at most MAX_EDGE px (which also drops its location data).
// A file the browser can't decode is returned as it is, so the server can say what's wrong with it.
async function shrink(file) {
  let bitmap
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" })
  } catch {
    return file
  }

  try {
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height))
    const canvas = document.createElement("canvas")
    canvas.width = Math.round(bitmap.width * scale)
    canvas.height = Math.round(bitmap.height * scale)

    const context = canvas.getContext("2d")
    context.fillStyle = "#fff" // JPEG has no transparency
    context.fillRect(0, 0, canvas.width, canvas.height)
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height)

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY))
    return blob ? new File([blob], file.name.replace(/\.[^.]*$/, "") + ".jpg", { type: "image/jpeg" }) : file
  } catch {
    return file
  } finally {
    bitmap.close?.()
  }
}
